import { AuthApiError } from './auth-client';

/**
 * Known auth error identifiers → i18n key. A service error arrives either with
 * a machine-readable `code` (structured errors, e.g. `telegram_account_taken`)
 * or — for string exceptions like `ConflictException('email_taken')` — with the
 * identifier in `message` while `code` is the generic `http_error`. We look up
 * both.
 */
const KEY_BY_IDENTIFIER: Record<string, string> = {
  email_taken: 'auth.error.emailTaken',
  telegram_account_taken: 'telegram.connect.taken',
  // A bot sign-in finished in a browser now signed in as somebody else than
  // the account it was started for (security review, 18.10): nothing linked.
  telegram_link_mismatch: 'telegram.connect.mismatch',
  telegram_shell_has_team: 'telegram.connect.hasTeam',
  // The duplicate Telegram account carries a subscription, payment card, API key
  // or other data of its own, so the server refuses to merge it rather than
  // cascade-deleting it. One code covers the whole class (see the API's
  // USER_RELATION_LEDGER) — the remedy is the same whichever table it was.
  telegram_shell_has_assets: 'telegram.connect.hasAssets',
  // Login: the server returns one generic identifier for both an unknown email
  // and a wrong password (no user enumeration) — keep the message general.
  'Invalid credentials': 'auth.error.invalidCredentials',
  // Email OTP. Unmapped, this fell through to "что-то пошло не так", which on
  // the PRIMARY signup path is the worst possible wording: a mistyped digit and
  // a broken server read identically, so the one action that fixes it — type it
  // again — is the one the message argues against. Naming it is safe: reaching
  // this response already required knowing a live code request for the address,
  // so it leaks nothing an enumeration probe could use.
  invalid_code: 'auth.error.invalidCode',
  invalid_phone: 'auth.error.invalidPhone',
};

/** i18n key for "that address is not shaped like an address". Exported so the
 *  routes can show the SAME sentence for their pre-flight check as the one the
 *  server's rejection resolves to — two wordings for one problem is how a user
 *  concludes the second one is a different, new failure. */
export const EMAIL_SYNTAX_KEY = 'auth.error.emailSyntax';

/**
 * i18n key for "that code is not six digits yet".
 *
 * Deliberately the key `register.codeIncomplete` — the one the signup screen and
 * the guest payment sheet already show for the identical mistake — and NOT
 * `auth.error.invalidCode`. The two describe different problems and only one of
 * them is true here: `invalidCode` reads «Код не подошёл: возможно, он устарел
 * или его заменил более новый. Запросите новый код», which sends a person who
 * simply typed four digits off to mint a NEW code — consuming the live one in
 * their mailbox and putting them back where they started. A length hint is the
 * only wording that names what they can act on.
 *
 * Exported for the same reason `EMAIL_SYNTAX_KEY` is: the routes' pre-flight and
 * the server's rejection must resolve to one sentence, or the second looks like
 * a new, different failure.
 */
export const CODE_LENGTH_KEY = 'register.codeIncomplete';

/**
 * i18n key for "too many code requests — wait N minutes, or use the link in the
 * last letter". Takes `{{minutes}}`; see `otpWaitMinutes`.
 */
export const OTP_RATE_LIMIT_KEY = 'auth.otp.rateLimited';

/**
 * Seconds until a login code can be asked for again, when `err` is the API's
 * 429 `rate_limited` on the code routes (`/auth/otp/request`,
 * `/auth/me/email/request`) — null for anything else.
 *
 * Keyed on `retryAfterSec` being present, not on the code alone: the Telegram
 * bot sign-in also answers `rate_limited`, without a wait, and «подождите N
 * минут… ссылка из письма» would be the wrong sentence there.
 */
export function otpRateLimitSec(err: unknown): number | null {
  if (!(err instanceof AuthApiError) || err.code !== 'rate_limited') return null;
  const sec = err.retryAfterSec;
  return typeof sec === 'number' && Number.isFinite(sec) && sec > 0 ? sec : null;
}

/** Whole minutes for the «подождите N мин.» line — rounded UP, never 0: «0 мин.»
 *  would send someone back to a button that is still refused. */
export function otpWaitMinutes(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60));
}

/** Exactly six digits — what `VerifyOtpDto`'s `@Length(6, 6)` accepts, nothing
 *  looser and nothing stricter. Trims first, as the routes `trim()` before
 *  sending. */
export function isCompleteCode(value: string): boolean {
  return /^\d{6}$/.test(value.trim());
}

/**
 * Is this shaped like an address the API will accept?
 *
 * ⚠️ Deliberately NOT stricter than the server. `class-validator`'s `IsEmail`
 * rejects `ivan@gmail`, `ivan@mail` and `ivan@gmail.c` — no dot in the domain,
 * or a one-letter TLD — while `<input type="email">` accepts all three, because
 * the HTML spec's definition allows a bare host (`a@b`). That gap is the whole
 * bug: the browser waved them through, the API answered 400 `validation_error`,
 * and the only thing the user saw was «Что-то пошло не так» — a message that
 * blames us and gives them nothing to fix. Measured on prod 2026-09-11: 14
 * people in one day, one of them hitting it four times in 40 seconds before
 * leaving.
 *
 * So this checks exactly the two things the server checks and NOTHING else:
 * a dot in the domain and a TLD of at least two letters. No opinion about `+`
 * tags, digits, dashes, sub-domains or long TLDs — inventing a rule the server
 * does not have would block addresses that work, which is strictly worse than
 * the bug being fixed (it fails BEFORE the request, so the server can never
 * overrule it).
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-zA-Z]{2,}$/;

/** Trims first: a trailing space is the commonest paste artefact and the routes
 *  already `trim()` before sending, so validating the raw value would reject an
 *  address that would have gone through. */
export function isLikelyEmail(value: string): boolean {
  return EMAIL_SHAPE.test(value.trim());
}

/**
 * Resolve an auth API error to a localized, user-facing message so a raw server
 * code (e.g. "email_taken") never reaches the user. Unknown errors fall back to
 * a generic message.
 */
export function resolveAuthError(
  err: unknown,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  /*
   * A spent login-code ceiling (22.10). No code was sent, so «что-то пошло не
   * так» is wrong and so is silence — say how long, and that the letter already
   * in the mailbox still works.
   */
  const waitSec = otpRateLimitSec(err);
  if (waitSec !== null) return t(OTP_RATE_LIMIT_KEY, { minutes: otpWaitMinutes(waitSec) });
  if (err instanceof AuthApiError) {
    /*
     * `validation_error` is a CLASS, not an identifier — the DTO layer emits it
     * for every rejected field — so it cannot live in KEY_BY_IDENTIFIER, which
     * maps one code to one sentence. Two fields have a sentence worth saying;
     * anything else keeps the generic fallback rather than telling someone their
     * email is wrong when the server complained about a phone.
     */
    if (err.code === 'validation_error' && err.field === 'email') return t(EMAIL_SYNTAX_KEY);
    /*
     * A short code. Measured 2026-09-21: someone confirming their address from
     * the «Настройка профиля» strip pressed «Подтвердить» with fewer than six
     * digits, `VerifyOtpDto`'s `@Length(6, 6)` answered 400 `validation_error`
     * {field:'code'}, and the only branch above was `email` — so it landed on
     * «Что-то пошло не так. Попробуйте ещё раз.», which blames us for their
     * typo and names no action.
     *
     * `field` is the DTO property name (the filter takes it from the first word
     * of class-validator's message), and BOTH DTOs that carry one — `VerifyOtpDto`
     * and `VerifyEmailChangeDto` — declare it `@Length(6, 6)`. So every
     * `validation_error` about `code` the auth surface can produce is a
     * length complaint, and one sentence covers the class.
     */
    if (err.code === 'validation_error' && err.field === 'code') return t(CODE_LENGTH_KEY);
    const key = KEY_BY_IDENTIFIER[err.code ?? ''] ?? KEY_BY_IDENTIFIER[err.message];
    if (key) return t(key);
  }
  return t('auth.error.generic');
}
